/**
 * seasons.tsx
 * 
 * @copyright jnakaso.com
 * 
 * Seasons page will contain
 * <li>GOY</li>
 * <li>Qualifiers</li>
 * <li>Match Play Results</li>
 * <li>Tournament Results</li>
 * 
 */

import { Card, Center, Group, ScrollArea, Select, Stack, Tabs, TabsList, TabsPanel, Text } from "@mantine/core";
import { useContext, useEffect, useState } from "react";
import { TournamentBracket } from "src/components/cards/matchPlayCard";
import QualifierCard from "src/components/cards/qualifierCard";
import TournamentCard from "src/components/cards/tournamentCard";
import TwoDayCard from "src/components/cards/twoDayCard";
import { SeasonContext, SettingsContext } from "src/components/contexts";
import { storageService } from "src/services/storageService";
import { GoyCard } from "src/components/cards/goyCard";
import { tournamentService } from "src/services/tournamentService";
import { seasonService } from "src/services/seasonService";
import { useSearchParams } from "react-router-dom";

function SQualifer() {
    const { season } = useContext(SeasonContext);
    const [qualifiers, setQualifiers] = useState<any>([]);

    useEffect(() => {
        if (season) {
            storageService.getSeasonFile(season, 'qualifiers.json')
                .then(t => setQualifiers(JSON.parse(t)))
                .catch(err => console.error(err));
        }
    }, [season]);

    return <Center style={{ marginTop: "1rem" }} >
        <QualifierCard season={season} qualifiers={qualifiers} />
    </Center>
}

function SGoy() {
    const { season } = useContext(SeasonContext);
    const [standings, setStandings] = useState<any>([]);
    useEffect(() => {
        if (season) {
            storageService.getSeasonFile(season, 'goy.json')
                .then(t => setStandings(JSON.parse(t)))
                .catch(err => console.error(err));
        }
    }, [season]);

    return <Stack style={{ marginTop: "1rem" }} >
        <GoyCard season={season} standings={standings} />
    </Stack>
}

function SMatch() {
    const { season } = useContext(SeasonContext);
    const [aGrossMatch, setGrossMatch] = useState([]);
    const [aNetMatch, setNetMatch] = useState([]);
    const [bMatch, setBMatch] = useState([]);

    useEffect(() => {
        if (season) {
            storageService.getSeasonFile(season, 'a-gross-match.json')
                .then(t => setGrossMatch(JSON.parse(t)))
                .catch(err => console.error(err));
            storageService.getSeasonFile(season, 'a-net-match.json')
                .then(t => setNetMatch(JSON.parse(t)))
                .catch(err => console.error(err));
            storageService.getSeasonFile(season, 'b-match.json')
                .then(t => setBMatch(JSON.parse(t)))
                .catch(err => console.log(err));
        }
    }, [season])
    return <Stack style={{ marginTop: "1rem" }} gap={'lg'}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Card.Section>
                <Center h={60} bg="var(--mantine-color-gray-light)">
                    <Text fw={800} >A-Flight Gross</Text>
                </Center>
            </Card.Section>
            <TournamentBracket bracket={aGrossMatch} />
        </Card>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Card.Section>
                <Center h={60} bg="var(--mantine-color-gray-light)">
                    <Text fw={800} >A-Flight Net</Text>
                </Center>
            </Card.Section>
            <TournamentBracket bracket={aNetMatch} />
        </Card>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Card.Section>
                <Center h={60} bg="var(--mantine-color-gray-light)">
                    <Text fw={800} >B-Flight Net</Text>
                </Center>
            </Card.Section>
            <TournamentBracket bracket={bMatch} />
        </Card>
    </Stack>
}

function STournaments() {
    const { season } = useContext(SeasonContext);
    const [tournaments, setTournaments] = useState<any[] | null>();

    useEffect(() => {
        if (season) {
            seasonService.findByYear(season)
                .then(ss => {
                    tournamentService.findBySeason(ss!)
                        .then(tt => setTournaments(tt.reverse()))
                });
        }
    }, [season])

    return <Stack style={{ marginTop: "1rem" }} gap={'lg'}>
        {tournaments && tournaments.map((tournament, idx) =>
            <TournamentCard key={idx} tournament={tournament} />
        )}
    </Stack>
}

function TwoDay(props: { label: string, file: string }) {
    const { season } = useContext(SeasonContext);
    const [tournament, setTournament] = useState<any>();

    useEffect(() => {
        if (props.file && season) {
            setTournament(undefined);
            storageService.getSeasonFile(season, props.file)
                .then(t => setTournament(JSON.parse(t)))
                .catch(err => {
                    console.error(err)
                });
        }
    }, [props, season])

    return <Stack style={{ marginTop: "1rem" }} >
        <TwoDayCard tournament={tournament} title={`${season} ${props.label}`} />
    </Stack>
}

const sections = [
    { value: 'goy', label: 'G.O.Y.', element: <SGoy /> },
    { value: 'tournaments', label: 'Tournaments', element: <STournaments /> },
    { value: 'spring', label: 'Spring', element: <TwoDay label="Spring" file="spring.json" /> },
    { value: 'fall', label: 'Fall', element: <TwoDay label="Fall" file="fall.json" /> },
    { value: 'nikkei', label: 'Nikkei', element: <TwoDay label="Nikkei" file="nikkei.json" /> },
    { value: 'match', label: 'Match Play', element: <SMatch /> },
    { value: 'qualifiers', label: 'KP Qualifiers', element: <SQualifer /> }
]

function Seasons() {
    const [searchParams] = useSearchParams();
    const { season, setSeason } = useContext(SeasonContext);
    const { settings } = useContext(SettingsContext);
    const [seasons, setSeasons] = useState<string[]>([]);
    const [section, setSection] = useState<string>(sections[0].value);

      useEffect(() => {
        if (searchParams) {
            const sect = searchParams.get('section')
            if (sect) {
                const s = sections.find(s => s.value === sect);
                if (s) {
                    setSection(s.value);
                }
            } else {
                setSection(sections[0].value);
            }
        }
    }, [searchParams])

    useEffect(() => {
        if (settings) {
            setSeasons(settings.seasons)
        }
    }, [settings])

    const changeSeason = (value) => {
        setSeason(Number(value))
    }

    const changeSection = (value) => {
        setSection(value)
    }

    return (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Card.Section>
                <Center h={60} bg="var(--mantine-color-gray-light)">
                    <Text fw={800} >{season} Season</Text>
                </Center>
            </Card.Section>
            <Tabs value={section}>
                <TabsList style={{ padding: 4 }}>
                    <Group justify="start" style={{ flex: 1 }}>
                        <Select
                            placeholder="Pick section"
                            value={section}
                            onChange={changeSection}
                            data={sections}
                        />
                    </Group>
                    <Group justify="end" style={{ flex: 1 }}>
                        <Select
                            placeholder="Pick season"
                            value={season.toString()}
                            onChange={changeSeason}
                            data={seasons}
                        />
                    </Group>
                </TabsList>
                {sections.map((s, idx) =>
                    <TabsPanel key={idx} value={s.value}>
                        <ScrollArea style={{ overflowY: "auto" }}>{s.element}</ScrollArea>
                    </TabsPanel>)}
            </Tabs>
        </Card>


    );

}

export default Seasons;